import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListEscalationRulesResult } from '@whinerp/client';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * Escalation rules (G-225): when a task, deal, lead or case goes undone for
 * too long, the owner and their manager - or a named person - hear, once.
 */

const OBJECTS = ['task', 'deal', 'lead', 'case'] as const;
const INPUT = 'rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink';

export function EscalationsPanel({ people }: { people: ReadonlyArray<{ userId: string; name: string }> }): JSX.Element | null {
  const t = useT();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [object, setObject] = useState<(typeof OBJECTS)[number]>('lead');
  const [hours, setHours] = useState('24');
  const [personId, setPersonId] = useState('');
  const rules = useQuery<ListEscalationRulesResult, ApiError>({ queryKey: ['escalation-rules'], queryFn: () => api.listEscalationRules({}) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['escalation-rules'] });
  const add = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.saveEscalationRule({
        body: { name: name.trim(), object, afterHours: Number(hours), notify: personId ? 'named' : 'owner-manager', targetUserId: personId || null },
      }),
    onSuccess: async () => {
      setName('');
      await refresh();
    },
  });
  const remove = useMutation<unknown, ApiError, string>({ mutationFn: (ruleId) => api.deleteEscalationRule({ path: { ruleId } }), onSuccess: () => refresh() });
  if (!rules.data) return null;
  const failure = add.error ?? remove.error;
  return (
    <Panel data-region="escalations">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('escalationRules.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('escalationRules.body')}</p>
        </div>
        {failure ? <p data-escalation-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">{failure.message}</p> : null}
        <ul className="flex flex-col">
          {rules.data.rules.map((rule) => (
            <li key={rule.ruleId} data-escalation-rule={rule.ruleId} className="flex flex-wrap items-center gap-2 border-t border-t-rule py-2 first:border-t-0">
              <span className="min-w-0 flex-1 truncate text-body text-ink">{rule.name}</span>
              <span className="text-meta text-ink-meta" data-escalation-summary>
                {t(`escalationRules.when.${rule.object}`, { values: { hours: String(rule.afterHours) } })}
                {' · '}
                {rule.notify === 'named' ? t('escalationRules.tells', { values: { name: rule.targetUserName ?? '-' } }) : t('escalationRules.tellsManager')}
                {' · '}
                {t('escalationRules.fired', { values: { count: String(rule.fired) } })}
              </span>
              <Button intent="ghost" size="small" onClick={() => remove.mutate(rule.ruleId)}>{t('action.delete')}</Button>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('escalationRules.name')}
            <input data-escalation-name className={INPUT} value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('escalationRules.object')}
            <select data-escalation-object className={INPUT} value={object} onChange={(event) => setObject(event.target.value as 'lead')}>
              {OBJECTS.map((key) => <option key={key} value={key}>{t(`escalationRules.object.${key}`)}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('escalationRules.after')}
            <input data-escalation-hours inputMode="numeric" className={`${INPUT} w-col-count`} value={hours} onChange={(event) => setHours(event.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('escalationRules.who')}
            <select data-escalation-person className={INPUT} value={personId} onChange={(event) => setPersonId(event.target.value)}>
              <option value="">{t('escalationRules.tellsManager')}</option>
              {people.map((person) => <option key={person.userId} value={person.userId}>{person.name}</option>)}
            </select>
          </label>
          <Button intent="secondary" size="small" data-escalation-add disabled={name.trim() === '' || !/^\d+$/.test(hours)} onClick={() => add.mutate()}>
            {t('escalationRules.add')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
